import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Table, Form, Spinner, Button, Badge, Alert, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';

const formatCurrency = (n) =>
  parseFloat(n || 0).toLocaleString('es-ES', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const formatFecha = (f) => (f ? new Date(f).toLocaleDateString('es-ES') : '—');

const descargarCSV = (nombre, filas) => {
  const csv = filas.map(f => f.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
};

const Tarjeta = ({ color, icono, valor, titulo, nota }) => (
  <Card className={`stats-card border-${color} h-100`}>
    <Card.Body>
      <div className={`stats-icon text-${color}`}><i className={`fas ${icono}`}></i></div>
      <div className={`stats-number text-${color}`}>{valor}</div>
      <div className="stats-label">{titulo}</div>
      {nota && <small className="text-muted">{nota}</small>}
    </Card.Body>
  </Card>
);

const InteresesCobrados = () => {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [socioFiltro, setSocioFiltro] = useState('');
  const [añoFiltro, setAñoFiltro] = useState('');
  const [sociosDisponibles, setSociosDisponibles] = useState([]);
  const [añosDisponibles, setAñosDisponibles] = useState([]);
  const [pestaña, setPestaña] = useState('activos');

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (socioFiltro) params.append('socio', socioFiltro);
      if (añoFiltro) params.append('año', añoFiltro);
      const res = await axios.get(`/api/reportes-prestamos/intereses-finalizados?${params.toString()}`);
      setDatos(res.data);

      // Las listas de filtros se arman con la carga sin filtros
      if (!socioFiltro && !añoFiltro) {
        const mapa = new Map();
        [...res.data.porSocio, ...res.data.activos.porSocio].forEach(s => mapa.set(s.socioId, s.socio));
        setSociosDisponibles([...mapa].map(([id, nombre]) => ({ id, nombre })).sort((a, b) => a.nombre.localeCompare(b.nombre)));
        setAñosDisponibles(res.data.añosDisponibles || []);
      }
    } catch (error) {
      console.error('Error al cargar el reporte:', error);
      toast.error('Error al cargar el reporte de intereses');
    } finally {
      setLoading(false);
    }
  }, [socioFiltro, añoFiltro]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const exportar = () => {
    if (!datos) return;
    const fecha = formatFecha(datos.general.fechaCorte);
    if (pestaña === 'activos') {
      const a = datos.activos;
      descargarCSV(`intereses-activos-al-${fecha.replace(/\//g, '-')}.csv`, [
        [`Intereses de préstamos activos al ${fecha}`],
        ['Solicitud', 'Socio', 'Cédula', 'Monto', 'Interés mensual', 'Meses corridos', 'Interés generado', 'Interés pagado', 'Interés vencido', 'Adelantado', 'Debe hoy'],
        ...a.detalle.map(d => [d.numeroSolicitud, d.socio, d.cedula, d.monto, d.interesMensual, d.mesesTranscurridos,
          d.interesGenerado, d.interesPagado, d.interesVencido, d.interesAdelantado, d.deudaHoy]),
        [],
        ['TOTAL', '', '', '', '', '', a.totales.interesGenerado, a.totales.interesPagado, a.totales.interesVencido, '', a.totales.deudaHoy]
      ]);
    } else {
      descargarCSV(`intereses-finalizados${añoFiltro ? '-' + añoFiltro : ''}.csv`, [
        ['Solicitud', 'Socio', 'Cédula', 'Monto', 'Plazo', 'Interés pactado', 'Capital pagado', 'Interés pagado', 'Finalizado'],
        ...datos.detalle.map(d => [d.numeroSolicitud, d.socio, d.cedula, d.monto, d.plazo, d.interesPactado,
          d.capitalPagado, d.interesPagado, formatFecha(d.fechaFinalizacion)]),
        [],
        ['TOTAL', '', '', datos.totales.montoPrestado, '', datos.totales.interesPactado, datos.totales.capitalPagado, datos.totales.interesPagado, '']
      ]);
    }
  };

  const g = datos?.general;
  const fin = datos?.totales;
  const act = datos?.activos?.totales;
  const nombreSocio = socioFiltro ? sociosDisponibles.find(s => s.id === socioFiltro)?.nombre : null;

  return (
    <Container fluid className="mt-4">
      <div className="fade-in">
        <Row className="mb-4">
          <Col>
            <Card className="card-custom">
              <Card.Body>
                <h2 className="text-gradient mb-2">
                  <i className="fas fa-hand-holding-usd me-2"></i>
                  Intereses de Préstamos
                </h2>
                <p className="text-muted mb-0">
                  Interés cobrado y generado hasta hoy{g ? ` (${formatFecha(g.fechaCorte)})` : ''}. Cada mes que pasa,
                  los préstamos activos suman un mes más de interés.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col md={5}>
            <Form.Group>
              <Form.Label><i className="fas fa-user me-2"></i>Socio</Form.Label>
              <Form.Select value={socioFiltro} onChange={(e) => setSocioFiltro(e.target.value)}>
                <option value="">Todos los socios</option>
                {sociosDisponibles.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={7} className="d-flex align-items-end justify-content-md-end mt-3 mt-md-0 gap-2">
            {socioFiltro && (
              <Button variant="outline-secondary" onClick={() => setSocioFiltro('')}>Ver todos los socios</Button>
            )}
            <Button variant="success" onClick={exportar} disabled={!datos}>
              <i className="fas fa-file-excel me-2"></i>Exportar pestaña
            </Button>
          </Col>
        </Row>

        {loading && !datos ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : !datos ? null : (
          <>
            <Row className="mb-4">
              <Col md={3} className="mb-3">
                <Tarjeta color="success" icono="fa-coins" valor={formatCurrency(g.interesCobradoTotal)}
                  titulo={`Interés cobrado total${nombreSocio ? ` — ${nombreSocio}` : ''}`}
                  nota={`Finalizados ${formatCurrency(g.interesCobradoFinalizados)} + activos ${formatCurrency(g.interesCobradoActivos)}`} />
              </Col>
              <Col md={3} className="mb-3">
                <Tarjeta color="primary" icono="fa-chart-line" valor={formatCurrency(g.interesGeneradoActivos)}
                  titulo="Interés generado hasta hoy (activos)"
                  nota={`Interés mensual × meses corridos, en ${act.prestamos} préstamo(s). De esto ya se pagó ${formatCurrency(act.interesPagado)}.`} />
              </Col>
              <Col md={3} className="mb-3">
                <Tarjeta color="danger" icono="fa-exclamation-triangle" valor={formatCurrency(g.interesPorCobrar)}
                  titulo="Interés vencido por cobrar"
                  nota={act.interesAdelantado > 0
                    ? `${formatCurrency(act.interesGenerado)} generado − ${formatCurrency(act.interesPagado)} pagado + ${formatCurrency(act.interesAdelantado)} adelantado (el adelanto de un socio no descuenta la deuda de otro)`
                    : `${formatCurrency(act.interesGenerado)} generado − ${formatCurrency(act.interesPagado)} pagado`} />
              </Col>
              <Col md={3} className="mb-3">
                <Tarjeta color="secondary" icono="fa-check-double" valor={formatCurrency(fin.interesPagado)}
                  titulo="Interés de préstamos finalizados" nota={`${fin.prestamos} préstamo(s) cerrado(s)`} />
              </Col>
            </Row>

            <Tabs activeKey={pestaña} onSelect={(k) => setPestaña(k)} className="mb-3">
              {/* ---------------- ACTIVOS ---------------- */}
              <Tab eventKey="activos" title={`📈 Activos, hasta hoy (${act.prestamos})`}>
                {act.prestamos === 0 ? (
                  <Alert variant="info">No hay préstamos activos{nombreSocio ? ` de ${nombreSocio}` : ''}.</Alert>
                ) : (
                  <>
                    {!socioFiltro && (
                      <Card className="card-custom mb-4">
                        <Card.Header className="card-header-custom">
                          <h5 className="mb-0"><i className="fas fa-users me-2"></i>Por socio</h5>
                        </Card.Header>
                        <Card.Body>
                          <div className="table-responsive">
                            <Table hover>
                              <thead>
                                <tr>
                                  <th>Socio</th>
                                  <th className="text-end">Interés generado</th>
                                  <th className="text-end">Interés pagado</th>
                                  <th className="text-end">Interés vencido</th>
                                  <th className="text-end">Debe hoy</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {datos.activos.porSocio.map(s => (
                                  <tr key={s.socioId}>
                                    <td>
                                      <div className="fw-bold">{s.socio}</div>
                                      <small className="text-muted">{s.numeroSocio}{s.cedula ? ` · ${s.cedula}` : ''}</small>
                                    </td>
                                    <td className="text-end">{formatCurrency(s.interesGenerado)}</td>
                                    <td className="text-end text-success">{formatCurrency(s.interesPagado)}</td>
                                    <td className={`text-end ${s.interesVencido > 0 ? 'text-danger fw-bold' : 'text-muted'}`}>{formatCurrency(s.interesVencido)}</td>
                                    <td className="text-end">{formatCurrency(s.deudaHoy)}</td>
                                    <td className="text-center">
                                      <Button size="sm" variant="outline-primary" onClick={() => setSocioFiltro(s.socioId)}>
                                        <i className="fas fa-eye me-1"></i>Ver
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="table-light fw-bold">
                                  <td>Total</td>
                                  <td className="text-end">{formatCurrency(act.interesGenerado)}</td>
                                  <td className="text-end text-success">{formatCurrency(act.interesPagado)}</td>
                                  <td className="text-end text-danger">{formatCurrency(act.interesVencido)}</td>
                                  <td className="text-end">{formatCurrency(act.deudaHoy)}</td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>
                    )}

                    <Card className="card-custom">
                      <Card.Header className="card-header-custom">
                        <h5 className="mb-0"><i className="fas fa-list me-2"></i>Préstamos activos{nombreSocio ? ` de ${nombreSocio}` : ''}</h5>
                      </Card.Header>
                      <Card.Body>
                        <div className="table-responsive">
                          <Table hover size="sm">
                            <thead>
                              <tr>
                                <th>Solicitud</th>
                                {!socioFiltro && <th>Socio</th>}
                                <th className="text-end">Monto</th>
                                <th className="text-end">Interés/mes</th>
                                <th className="text-center">Meses corridos</th>
                                <th className="text-end">Generado</th>
                                <th className="text-end">Pagado</th>
                                <th className="text-end">Vencido</th>
                                <th className="text-end">Debe hoy</th>
                              </tr>
                            </thead>
                            <tbody>
                              {datos.activos.detalle.map(d => (
                                <tr key={d.prestamoId}>
                                  <td className="fw-bold">{d.numeroSolicitud}</td>
                                  {!socioFiltro && <td>{d.socio}</td>}
                                  <td className="text-end">{formatCurrency(d.monto)}</td>
                                  <td className="text-end">{formatCurrency(d.interesMensual)}</td>
                                  <td className="text-center">{d.mesesTranscurridos}</td>
                                  <td className="text-end">{formatCurrency(d.interesGenerado)}</td>
                                  <td className="text-end text-success">
                                    {formatCurrency(d.interesPagado)}
                                    {d.interesAdelantado > 0 && (
                                      <div><small className="text-info">{formatCurrency(d.interesAdelantado)} adelantado</small></div>
                                    )}
                                  </td>
                                  <td className={`text-end ${d.interesVencido > 0 ? 'text-danger fw-bold' : 'text-muted'}`}>{formatCurrency(d.interesVencido)}</td>
                                  <td className="text-end">{formatCurrency(d.deudaHoy)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="table-light fw-bold">
                                <td colSpan={socioFiltro ? 4 : 5}>Total</td>
                                <td className="text-end">{formatCurrency(act.interesGenerado)}</td>
                                <td className="text-end text-success">{formatCurrency(act.interesPagado)}</td>
                                <td className="text-end text-danger">{formatCurrency(act.interesVencido)}</td>
                                <td className="text-end">{formatCurrency(act.deudaHoy)}</td>
                              </tr>
                              {act.interesAdelantado > 0 && (
                                <tr className="table-light">
                                  <td colSpan={socioFiltro ? 8 : 9}>
                                    <small className="text-muted">
                                      Generado {formatCurrency(act.interesGenerado)} − pagado {formatCurrency(act.interesPagado)} = {formatCurrency(act.interesGenerado - act.interesPagado)}.
                                      El vencido es {formatCurrency(act.interesVencido)} porque {formatCurrency(act.interesAdelantado)} se pagaron por adelantado
                                      y ese adelanto queda a favor de ese socio, no descuenta la deuda de otros.
                                    </small>
                                  </td>
                                </tr>
                              )}
                            </tfoot>
                          </Table>
                        </div>
                        <small className="text-muted">
                          "Generado" = interés mensual × meses que ya corrieron hasta hoy. "Vencido" = generado que todavía
                          no se pagó. Se recalcula solo cada mes.
                        </small>
                      </Card.Body>
                    </Card>
                  </>
                )}
              </Tab>

              {/* ---------------- FINALIZADOS ---------------- */}
              <Tab eventKey="finalizados" title={`✅ Finalizados (${fin.prestamos})`}>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Select value={añoFiltro} onChange={(e) => setAñoFiltro(e.target.value)}>
                      <option value="">Todos los años de finalización</option>
                      {añosDisponibles.map(a => <option key={a} value={a}>Finalizados en {a}</option>)}
                    </Form.Select>
                  </Col>
                </Row>

                {fin.prestamos === 0 ? (
                  <Alert variant="info">No hay préstamos finalizados con estos filtros.</Alert>
                ) : (
                  <>
                    {!socioFiltro && (
                      <Card className="card-custom mb-4">
                        <Card.Header className="card-header-custom">
                          <h5 className="mb-0"><i className="fas fa-users me-2"></i>Por socio</h5>
                        </Card.Header>
                        <Card.Body>
                          <div className="table-responsive">
                            <Table hover>
                              <thead>
                                <tr>
                                  <th>Socio</th>
                                  <th className="text-center">Préstamos</th>
                                  <th className="text-end">Monto prestado</th>
                                  <th className="text-end">Capital pagado</th>
                                  <th className="text-end">Interés pactado</th>
                                  <th className="text-end">Interés pagado</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                {datos.porSocio.map(s => (
                                  <tr key={s.socioId}>
                                    <td>
                                      <div className="fw-bold">{s.socio}</div>
                                      <small className="text-muted">{s.numeroSocio}{s.cedula ? ` · ${s.cedula}` : ''}</small>
                                    </td>
                                    <td className="text-center"><Badge bg="secondary">{s.prestamos}</Badge></td>
                                    <td className="text-end">{formatCurrency(s.montoPrestado)}</td>
                                    <td className="text-end">{formatCurrency(s.capitalPagado)}</td>
                                    <td className="text-end text-muted">{formatCurrency(s.interesPactado)}</td>
                                    <td className="text-end fw-bold text-success">{formatCurrency(s.interesPagado)}</td>
                                    <td className="text-center">
                                      <Button size="sm" variant="outline-primary" onClick={() => setSocioFiltro(s.socioId)}>
                                        <i className="fas fa-eye me-1"></i>Ver
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="table-light fw-bold">
                                  <td>Total</td>
                                  <td className="text-center">{fin.prestamos}</td>
                                  <td className="text-end">{formatCurrency(fin.montoPrestado)}</td>
                                  <td className="text-end">{formatCurrency(fin.capitalPagado)}</td>
                                  <td className="text-end">{formatCurrency(fin.interesPactado)}</td>
                                  <td className="text-end text-success">{formatCurrency(fin.interesPagado)}</td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            </Table>
                          </div>
                        </Card.Body>
                      </Card>
                    )}

                    <Card className="card-custom">
                      <Card.Header className="card-header-custom">
                        <h5 className="mb-0"><i className="fas fa-list me-2"></i>Préstamos finalizados{nombreSocio ? ` de ${nombreSocio}` : ''}</h5>
                      </Card.Header>
                      <Card.Body>
                        <div className="table-responsive">
                          <Table hover size="sm">
                            <thead>
                              <tr>
                                <th>Solicitud</th>
                                {!socioFiltro && <th>Socio</th>}
                                <th className="text-end">Monto</th>
                                <th className="text-center">Plazo</th>
                                <th>Aprobado</th>
                                <th>Finalizado</th>
                                <th className="text-end">Capital pagado</th>
                                <th className="text-end">Interés pactado</th>
                                <th className="text-end">Interés pagado</th>
                              </tr>
                            </thead>
                            <tbody>
                              {datos.detalle.map(d => (
                                <tr key={d.prestamoId}>
                                  <td className="fw-bold">{d.numeroSolicitud}</td>
                                  {!socioFiltro && <td>{d.socio}</td>}
                                  <td className="text-end">{formatCurrency(d.monto)}</td>
                                  <td className="text-center">{d.plazo} m</td>
                                  <td>{formatFecha(d.fechaAprobacion)}</td>
                                  <td>{formatFecha(d.fechaFinalizacion)}</td>
                                  <td className="text-end">{formatCurrency(d.capitalPagado)}</td>
                                  <td className="text-end text-muted">{formatCurrency(d.interesPactado)}</td>
                                  <td className="text-end fw-bold text-success">
                                    {formatCurrency(d.interesPagado)}
                                    {Math.abs(d.diferenciaInteres) >= 0.01 && (
                                      <div>
                                        <small className={d.diferenciaInteres > 0 ? 'text-info' : 'text-danger'}>
                                          {d.diferenciaInteres > 0 ? '+' : ''}{formatCurrency(d.diferenciaInteres)} vs pactado
                                        </small>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="table-light fw-bold">
                                <td colSpan={socioFiltro ? 5 : 6}>Total</td>
                                <td className="text-end">{formatCurrency(fin.capitalPagado)}</td>
                                <td className="text-end">{formatCurrency(fin.interesPactado)}</td>
                                <td className="text-end text-success">{formatCurrency(fin.interesPagado)}</td>
                              </tr>
                            </tfoot>
                          </Table>
                        </div>
                        <small className="text-muted">
                          El interés pagado puede ser mayor que el pactado si el socio se atrasó (el interés sigue corriendo
                          cada mes), o menor si terminó antes o se le condonó parte.
                        </small>
                      </Card.Body>
                    </Card>
                  </>
                )}
              </Tab>
            </Tabs>
          </>
        )}
      </div>
    </Container>
  );
};

export default InteresesCobrados;
